import { Badge, Box, BoxProps, ExpandableCard, Icon, Tooltip } from '@bitrise/bitkit';
import { scheduleBuildPath } from '@/utils/monolithApiRouteService';
import useMonolithApiQuery from '@/hooks/api/useMonolithApiQuery';
import AppForAppPage from '@/pages/App/types/AppForAppPage';
import { ScheduledBuild, ScheduledBuildResponse } from './scheduledBuilds.types';
import ScheduledBuildItem from './ScheduledBuildItem';

const style = {
  container: {
    color: 'purple.10',
    fontWeight: 'demiBold',
    display: 'flex',
    alignItems: 'center',
    gap: '4',
  } as BoxProps,
  counter: {
    display: 'inline-block',
    paddingX: '8',
    paddingY: '4',
    color: 'neutral.40',
    backgroundColor: 'neutral.93',
    borderRadius: '4',
    _groupActive: {
      color: 'neutral.10',
      backgroundColor: 'neutral.80',
    },
    _groupHover: {
      backgroundColor: 'neutral.90',
    },
  } as BoxProps,
};

const ButtonContent = ({ scheduledCount, invalidCount }: { scheduledCount: number; invalidCount: number }) => (
  <Box __css={style.container}>
    <Icon name="Calendar" size="16" marginRight="8" />
    <span>Scheduled builds</span>
    <Badge margin="0px 4px" sx={style.counter}>
      {scheduledCount}
    </Badge>
    {!!invalidCount && (
      <Tooltip label="Invalid schedules will not start builds until they are fixed. If you no longer need them, simply delete them.">
        <Badge variant="subtle" colorScheme="negative">
          {`${invalidCount} INVALID`}
        </Badge>
      </Tooltip>
    )}
  </Box>
);

export type ScheduledBuildsProps = {
  app: AppForAppPage;
  onUpdateScheduledBuild: (scheduledBuild: ScheduledBuild) => void;
};

const ScheduledBuilds = (props: ScheduledBuildsProps) => {
  const { app, onUpdateScheduledBuild } = props;
  const { data } = useMonolithApiQuery<ScheduledBuildResponse, null>(
    ['scheduledBuilds', app.slug],
    scheduleBuildPath(app.slug, {
      params: {
        build_trigger_token: app.buildTriggerToken,
      },
    }),
  );

  if (data?.schedules && data.schedules.length > 0) {
    const invalidCount = data.schedules.reduce((count, schedule) => {
      if (schedule.lastErrorMessage) {
        return count + 1;
      }
      return count;
    }, 0);
    return (
      <ExpandableCard
        buttonContent={<ButtonContent scheduledCount={data.schedules.length} invalidCount={invalidCount} />}
        border="1px solid"
        borderColor="neutral.80"
        borderRadius="8"
        backgroundColor="neutral.100"
      >
        {data.schedules.map((itemData, index) => (
          <ScheduledBuildItem
            key={itemData.id}
            data-testid={`scheduled-build-item-${index}`}
            app={app}
            scheduledBuildItemData={itemData}
            onUpdateScheduledBuild={onUpdateScheduledBuild}
            marginBlockEnd={index < data.schedules.length - 1 ? '16' : undefined}
            lastErrorMessage={itemData.lastErrorMessage}
          />
        ))}
      </ExpandableCard>
    );
  }

  return null;
};

export default ScheduledBuilds;
